import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Play, Pause, Trash2 } from 'lucide-react';
import { abTestingApi, type ABTest } from '../../api/endpoints/abTesting';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { EmptyState } from '../../components/ui/EmptyState';
import { useToast } from '../../components/ui/Alert';
import { formatDate } from '../../lib/formatters';

const STATUS_VARIANTS: Record<string, 'default' | 'info' | 'success' | 'warning' | 'danger'> = {
  draft: 'default',
  running: 'success',
  paused: 'warning',
  completed: 'info',
};

const PAGE_OPTIONS = [
  { value: 'home', label: 'Home' },
  { value: 'product', label: 'Product' },
  { value: 'category', label: 'Category' },
  { value: 'cart', label: 'Cart' },
];

export function ABTestingPage() {
  const { storeId } = useParams<{ storeId: string }>();
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [showCreate, setShowCreate] = useState(false);
  const [viewTest, setViewTest] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ABTest | null>(null);

  // Create form
  const [formName, setFormName] = useState('');
  const [formPage, setFormPage] = useState('home');
  const [formVariants, setFormVariants] = useState([
    { name: 'Control (Default)', versionId: 'default', weight: 50 },
    { name: 'Variant B', versionId: '', weight: 50 },
  ]);

  // Load page versions for the selected page
  const { data: versionsData } = useQuery({
    queryKey: ['page-versions-for-ab', storeId, formPage],
    queryFn: async () => {
      const { default: client } = await import('../../api/client');
      const res = await client.get(`/dashboard/${storeId}/pages/versions/${formPage}`);
      return (res.data as any).data?.versions ?? [];
    },
    enabled: !!storeId && showCreate,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['ab-tests', storeId],
    queryFn: () => abTestingApi.list(storeId!),
    enabled: !!storeId,
  });

  const { data: testDetail } = useQuery({
    queryKey: ['ab-test', storeId, viewTest],
    queryFn: () => abTestingApi.get(storeId!, viewTest!),
    enabled: !!storeId && !!viewTest,
  });

  const createMutation = useMutation({
    mutationFn: () => abTestingApi.create(storeId!, {
      name: formName,
      pageName: formPage,
      variants: formVariants.map(v => ({ name: v.name, versionId: v.versionId, weight: v.weight })),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ab-tests', storeId] });
      toast('success', t('abTesting.createSuccess', 'Experiment created'));
      setShowCreate(false);
    },
    onError: () => toast('error', t('common.error')),
  });

  const statusMutation = useMutation({
    mutationFn: ({ testId, status }: { testId: string; status: string }) =>
      abTestingApi.updateStatus(storeId!, testId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ab-tests', storeId] });
      toast('success', t('common.success'));
    },
    onError: () => toast('error', t('common.error')),
  });

  const deleteMutation = useMutation({
    mutationFn: (testId: string) => abTestingApi.delete(storeId!, testId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ab-tests', storeId] });
      toast('success', t('common.success'));
      setDeleteTarget(null);
    },
    onError: () => toast('error', t('common.error')),
  });

  function addVariant() {
    setFormVariants(prev => [...prev, { name: `Variant ${String.fromCharCode(65 + prev.length)}`, versionId: '', weight: 0 }]);
  }

  function updateVariant(index: number, patch: Partial<{ name: string; versionId: string; weight: number }>) {
    setFormVariants(prev => prev.map((v, i) => i === index ? { ...v, ...patch } : v));
  }

  function removeVariant(index: number) {
    if (formVariants.length <= 2) return;
    setFormVariants(prev => prev.filter((_, i) => i !== index));
  }

  const versionOptions = (versionsData || []).map((v: any) => ({
    value: v.versionId || v._id || 'default',
    label: v.versionName || v.versionId || 'Default',
  }));

  const tests = data?.tests ?? [];
  const detail = testDetail?.test;
  const stats = testDetail?.stats;

  if (isLoading) return <LoadingSpinner fullPage />;

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-bold">{t('abTesting.title', 'A/B Testing')}</h1>
        <Button onClick={() => { setFormName(''); setFormPage('home'); setFormVariants([{ name: 'Control (Default)', versionId: 'default', weight: 50 }, { name: 'Variant B', versionId: '', weight: 50 }]); setShowCreate(true); }}>
          <Plus className="h-4 w-4" />
          {t('abTesting.create', 'New Experiment')}
        </Button>
      </div>

      {tests.length === 0 ? (
        <EmptyState title={t('abTesting.empty', 'No experiments yet')} description={t('abTesting.emptyDesc', 'Create your first A/B test to optimize your store.')} />
      ) : (
        <div className="space-y-4">
          {tests.map((test) => (
            <div key={test._id} className="rounded-xl border border-border-default bg-bg-secondary p-5 hover:border-accent/30 transition-colors">
              <div className="flex items-start justify-between mb-3">
                <div className="cursor-pointer" onClick={() => setViewTest(test._id)}>
                  <h3 className="font-medium">{test.name}</h3>
                  <p className="text-xs text-text-muted">{test.pageName} page · {test.variants.length} {t('abTesting.variants', 'variants')}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={STATUS_VARIANTS[test.status] ?? 'default'}>{test.status}</Badge>
                  {test.status === 'draft' && (
                    <Button size="sm" variant="secondary" onClick={() => statusMutation.mutate({ testId: test._id, status: 'running' })}>
                      <Play className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {test.status === 'running' && (
                    <Button size="sm" variant="secondary" onClick={() => statusMutation.mutate({ testId: test._id, status: 'paused' })}>
                      <Pause className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {test.status === 'paused' && (
                    <Button size="sm" variant="secondary" onClick={() => statusMutation.mutate({ testId: test._id, status: 'running' })}>
                      <Play className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  <button onClick={() => setDeleteTarget(test)} className="rounded-lg p-1.5 text-text-muted hover:text-danger hover:bg-bg-hover">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Variant bars */}
              <div className="space-y-1.5">
                {test.variants.map((v) => {
                  const rate = v.conversionRate ?? 0;
                  const maxRate = Math.max(...test.variants.map(x => x.conversionRate ?? 0), 1);
                  return (
                    <div key={v.id} className="flex items-center gap-3">
                      <span className="text-xs w-24 truncate">{v.name}</span>
                      <div className="flex-1 h-2 rounded-full bg-bg-tertiary overflow-hidden">
                        <div className="h-full rounded-full bg-accent/70" style={{ width: `${(rate / maxRate) * 100}%` }} />
                      </div>
                      <span className="text-xs text-text-muted w-12 text-end">{rate.toFixed(1)}%</span>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-text-muted mt-2">{formatDate(test.createdAt)}</p>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <Modal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        title={t('abTesting.create', 'New Experiment')}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowCreate(false)}>{t('common.cancel')}</Button>
            <Button onClick={() => createMutation.mutate()} isLoading={createMutation.isPending} disabled={!formName.trim() || formVariants.some(v => !v.versionId)}>
              {t('common.create')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input label={t('abTesting.experimentName', 'Experiment Name')} required value={formName} onChange={(e) => setFormName(e.target.value)} />
          <Select
            label={t('abTesting.page', 'Page')}
            options={PAGE_OPTIONS}
            value={formPage}
            onChange={(e) => setFormPage(e.target.value)}
          />
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium">{t('abTesting.variants', 'Variants')}</label>
              <button onClick={addVariant} className="text-xs text-accent hover:underline">+ {t('common.add')}</button>
            </div>
            <div className="space-y-3">
              {formVariants.map((v, i) => (
                <div key={i} className="rounded-lg border border-border-default p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <Input placeholder="Variant name" value={v.name} onChange={(e) => updateVariant(i, { name: e.target.value })} className="flex-1" />
                    <Input type="number" min="0" max="100" value={v.weight} onChange={(e) => updateVariant(i, { weight: Number(e.target.value) || 0 })} className="w-20" />
                    <span className="text-xs text-text-muted">%</span>
                    {formVariants.length > 2 && (
                      <button onClick={() => removeVariant(i)} className="text-text-muted hover:text-danger p-1">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  <Select
                    label={t('abTesting.pageVersion', 'Page Version')}
                    options={[
                      { value: '', label: t('abTesting.selectVersion', '-- Select version --') },
                      ...versionOptions,
                    ]}
                    value={v.versionId}
                    onChange={(e) => updateVariant(i, { versionId: e.target.value })}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </Modal>

      {/* Detail Modal */}
      <Modal
        isOpen={!!viewTest}
        onClose={() => setViewTest(null)}
        title={detail?.name ?? ''}
        size="lg"
      >
        {detail && stats && (
          <div className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="rounded-lg bg-bg-tertiary p-3 text-center">
                <p className="text-xs text-text-muted">{t('abTesting.totalViews', 'Total Views')}</p>
                <p className="text-xl font-bold mt-1">{stats.totalViews}</p>
              </div>
              <div className="rounded-lg bg-bg-tertiary p-3 text-center">
                <p className="text-xs text-text-muted">{t('abTesting.totalConversions', 'Conversions')}</p>
                <p className="text-xl font-bold mt-1">{stats.totalConversions}</p>
              </div>
              <div className="rounded-lg bg-bg-tertiary p-3 text-center">
                <p className="text-xs text-text-muted">{t('abTesting.conversionRate', 'Conversion Rate')}</p>
                <p className="text-xl font-bold mt-1">{stats.overallConversionRate.toFixed(1)}%</p>
              </div>
            </div>

            <div className="space-y-3">
              {detail.variants.map((v) => (
                <div key={v.id} className="rounded-lg border border-border-default p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">{v.name}</span>
                    <Badge variant="default">
                      {v.weight}% {t('abTesting.traffic', 'traffic')}
                    </Badge>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-3 text-sm text-text-secondary">
                    <div>{t('abTesting.views', 'Views')}: <span className="font-medium text-text-primary">{v.views ?? 0}</span></div>
                    <div>{t('abTesting.conversions', 'Conversions')}: <span className="font-medium text-text-primary">{v.conversions ?? 0}</span></div>
                    <div>{t('abTesting.rate', 'Rate')}: <span className="font-medium text-text-primary">{(v.conversionRate ?? 0).toFixed(1)}%</span></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirm */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={t('common.confirm')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>{t('common.cancel')}</Button>
            <Button variant="danger" isLoading={deleteMutation.isPending} onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget._id)}>{t('common.delete')}</Button>
          </>
        }
      >
        <p className="text-sm text-text-secondary">{t('abTesting.deleteConfirm', 'Delete this experiment?')}</p>
      </Modal>
    </div>
  );
}
